import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DATASHEET_URL_MAX, DATASHEET_URL_RE, isSafeDatasheetUrl } from '@crm/shared'
import { setCustomsDatasheet } from '../../../shared/api/customs.api'
import { apiErrorText } from '../../../shared/api/errorText'
import { toast } from '../../../shared/ui/toastStore'

interface Props {
  itemId: string
  partNumber: string
  url: string | null
  /** Логист может вписать и поменять ссылку; остальные только открывают её. */
  canEdit: boolean
}

const IconPencil = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
)

/**
 * Ячейка «Даташит» очереди нетарифки. Ссылка открывается только если она
 * http(s) — иначе в поле могла бы оказаться javascript:-ссылка. У логиста —
 * карандаш: окно ввода ссылки (`PATCH /customs/items/:id/datasheet`).
 */
export function DatasheetCell({ itemId, partNumber, url, canEdit }: Props) {
  const [editing, setEditing] = useState(false)
  const safe = isSafeDatasheetUrl(url)

  return (
    <span className="inline-flex items-center gap-1.5">
      {safe ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          title={url}
          onClick={(e) => e.stopPropagation()}
          className="text-brand-600 hover:text-brand-700 font-medium"
        >
          Открыть
        </a>
      ) : (
        <span className="text-gray-400" title={url ? 'Ссылка не http(s) — не открываем' : ''}>
          —
        </span>
      )}
      {canEdit && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setEditing(true)
          }}
          title={url ? 'Изменить ссылку на даташит' : 'Добавить ссылку на даташит'}
          aria-label="Изменить даташит"
          className="text-gray-400 hover:text-brand-600 transition-colors"
        >
          <IconPencil />
        </button>
      )}
      {editing &&
        // Портал в body: строки AG Grid сдвинуты transform'ом, fixed-окно внутри
        // ячейки встало бы не по центру экрана.
        createPortal(
          <DatasheetModal
            itemId={itemId}
            partNumber={partNumber}
            url={url}
            onClose={() => setEditing(false)}
          />,
          document.body,
        )}
    </span>
  )
}

function DatasheetModal({
  itemId,
  partNumber,
  url,
  onClose,
}: {
  itemId: string
  partNumber: string
  url: string | null
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [value, setValue] = useState(url ?? '')
  const [error, setError] = useState('')

  const save = useMutation({
    mutationFn: (next: string | null) => setCustomsDatasheet(itemId, next),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['customs', 'queue'] })
      void qc.invalidateQueries({ queryKey: ['customs', 'item', itemId] })
      toast.success(res.datasheetUrl ? 'Ссылка на даташит сохранена' : 'Ссылка на даташит удалена')
      onClose()
    },
    onError: (e) => setError(apiErrorText(e, 'Не удалось сохранить ссылку на даташит')),
  })

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !save.isPending) onClose()
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose, save.isPending])

  const submit = () => {
    const trimmed = value.trim()
    if (!trimmed) {
      save.mutate(null)
      return
    }
    if (trimmed.length > DATASHEET_URL_MAX || !DATASHEET_URL_RE.test(trimmed)) {
      setError('Ссылка на даташит должна начинаться с http:// или https://')
      return
    }
    setError('')
    save.mutate(trimmed)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget && !save.isPending) onClose()
      }}
    >
      <div className="bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-md mx-4">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">Даташит · {partNumber}</h3>
          <button
            onClick={onClose}
            disabled={save.isPending}
            aria-label="Закрыть"
            className="text-gray-400 hover:text-gray-600 text-lg leading-none disabled:opacity-40"
          >
            ×
          </button>
        </div>
        <div className="p-4 space-y-3">
          <label className="block text-xs text-gray-500 mb-1" htmlFor="customs-datasheet-url">
            Ссылка на даташит (http:// или https://)
          </label>
          <input
            id="customs-datasheet-url"
            autoFocus
            value={value}
            maxLength={DATASHEET_URL_MAX}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                submit()
              }
            }}
            placeholder="https://…"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <p className="text-[11px] text-gray-400">Пустое поле — удалить ссылку.</p>
          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={save.isPending}
            className="px-4 py-1.5 border border-gray-300 text-gray-600 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Отмена
          </button>
          <button
            onClick={submit}
            disabled={save.isPending}
            className="px-4 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
          >
            {save.isPending ? 'Сохранение…' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  )
}
